// Questionnaire model shared by web, mobile and (mirrored in Go) the API.
// Keep in sync with api/internal/survey/definition.go.

export type QuestionType =
  | 'short_text'
  | 'long_text'
  | 'single_choice'
  | 'multiple_choice'
  | 'ranking'
  | 'dropdown'
  | 'number'
  | 'rating'
  | 'date'
  | 'yes_no'
  | 'matrix'
  | 'slider'
  | 'nps'
  | 'constant_sum'
  | 'text_form'
  | 'file_upload'
  | 'signature'
  | 'location'
  | 'descriptive';

export type Operator =
  | 'equals'
  | 'not_equals'
  | 'contains'
  | 'not_contains'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'answered'
  | 'not_answered';

/** Image or video attached to a question. */
export interface Media {
  type: 'image' | 'video';
  /** Uploaded file ("/api/media/<name>") or an https URL. Videos may be YouTube or Vimeo links. */
  url: string;
  /** Text alternative for images; caption for videos. */
  alt?: string;
  /** Video only: start playing when the question is shown. */
  autoplay?: boolean;
  /** Video only: start muted (respondents can unmute). */
  muted?: boolean;
}

export interface Option {
  id: string;
  label: string;
  /** Optional picture shown with the option ("/api/media/<name>" or https URL). */
  imageUrl?: string;
  /** Keeps its position when options are randomized (e.g. "Other", "None"). */
  fixed?: boolean;
  /** "Other, please specify": choosing it asks for text, stored under "<questionId>:other". */
  other?: boolean;
  /** Points added to the respondent's score when this option is chosen. */
  score?: number;
}

export type ConditionValue = string | number | boolean;

export interface Condition {
  questionId: string;
  /** Matrix row, constant-sum item or form field the condition looks at. */
  row?: string;
  operator: Operator;
  value?: ConditionValue;
}

export interface VisibleIf {
  match: 'all' | 'any';
  conditions: Condition[];
}

/** Every jump rule of a question in evaluation order. */
export function jumpsOf(q: { jumpIf?: JumpIf; jumpRules?: JumpIf[] }): JumpIf[] {
  return [...(q.jumpIf ? [q.jumpIf] : []), ...(q.jumpRules ?? [])];
}

export interface JumpIf {
  operator: Operator;
  value?: ConditionValue;
  /** A later question to skip to. Empty when the jump ends the survey (targetEndingId). */
  targetQuestionId: string;
  /** Ends the survey with this ending instead of skipping to a question. */
  targetEndingId?: string;
}

/** Copies options from an earlier choice question: the ones chosen, or the ones not chosen. */
export interface CarryForward {
  questionId: string;
  mode: 'selected' | 'not_selected';
}

/** An alternative end of the survey, reached through a jump rule. */
export interface Ending {
  id: string;
  /** Short name shown in the builder. */
  name: string;
  message: string;
  /** Optional https page the respondent is sent to after submitting. */
  redirectUrl?: string;
}

/** Stops collecting responses that match the conditions once `limit` were received. */
export interface Quota {
  id: string;
  name: string;
  match: 'all' | 'any';
  conditions: Condition[];
  limit: number;
  /** Shown to respondents who match a full quota; their response is not recorded. */
  message?: string;
}

export interface QuestionTranslation {
  title?: string;
  description?: string;
  tooltip?: string;
  placeholder?: string;
  minLabel?: string;
  maxLabel?: string;
  /** Option labels by option id. */
  options?: Record<string, string>;
  /** Matrix row labels by row id. */
  rows?: Record<string, string>;
}

/** One extra language's text. Anything left out falls back to the base language. */
export interface Translation {
  title?: string;
  description?: string;
  welcomeMessage?: string;
  thankYouMessage?: string;
  /** Section titles by the base-language title. */
  sections?: Record<string, string>;
  questions?: Record<string, QuestionTranslation>;
  /** Ending messages by ending id. */
  endings?: Record<string, string>;
  /** Quota messages by quota id. */
  quotas?: Record<string, string>;
}

export type ThemeFont = 'system' | 'serif' | 'rounded' | 'mono';
export const THEME_FONTS: ThemeFont[] = ['system', 'serif', 'rounded', 'mono'];

export type TextAlign = 'left' | 'center' | 'right' | 'justify';
export const TEXT_ALIGNS: TextAlign[] = ['left', 'center', 'right', 'justify'];
/** Display sizes a questionnaire can ask for (1 = standard); applied to what respondents see. */
export const THEME_ZOOMS: number[] = [0.9, 1, 1.1, 1.25, 1.5];

/** Respondent-facing look. Colors are #rrggbb. */
export interface Theme {
  primaryColor?: string;
  backgroundColor?: string;
  logoUrl?: string;
  font?: ThemeFont;
  /** Display size for respondents: one of THEME_ZOOMS; omitted means standard. */
  zoom?: number;
}

export interface Validation {
  min?: number;
  max?: number;
  minLength?: number;
  maxLength?: number;
  minSelections?: number;
  maxSelections?: number;
  integer?: boolean;
}

export interface Scale {
  min: number;
  max: number;
  /** Slider only: step between values (default 1). */
  step?: number;
  minLabel?: string;
  maxLabel?: string;
}

export interface Question {
  id: string;
  type: QuestionType;
  title: string;
  /** The title with formatting (see rich.ts). `title` always holds the same text without it. */
  titleRich?: string;
  /** How the question text and help text line up; omitted means left. */
  textAlign?: TextAlign;
  /** Optional stable human-readable code, unique within the questionnaire. */
  questionCode?: string;
  /** Optional page/section heading shared by questions in the same section. */
  sectionTitle?: string;
  description?: string;
  /** The help text with formatting (see rich.ts); `description` holds it without. */
  descriptionRich?: string;
  /** Short extra guidance shown behind an info icon next to the title. */
  tooltip?: string;
  /** Image or video shown above the answer. */
  media?: Media;
  required: boolean;
  options?: Option[];
  /** Matrix statements (rows); `options` are the answer columns. */
  rows?: Option[];
  /** Matrix: allow several columns per row. */
  matrixMultiple?: boolean;
  randomizeOptions?: boolean;
  /** Matrix: shuffle the rows. */
  randomizeRows?: boolean;
  /** Takes its options from an earlier choice question at answer time. */
  carryForward?: CarryForward;
  /** Constant sum: the amount the items must add up to (default 100). */
  total?: number;
  /** Never moves when question randomization is enabled. */
  fixedPosition?: boolean;
  /**
   * May be shuffled within its section even though it has display logic,
   * carry-forward or a jump (or other logic points at it). It still always
   * comes after the questions its conditions and carried options depend on.
   */
  shuffleWithLogic?: boolean;
  visibleIf?: VisibleIf;
  /** Skip forward to a later question when this question's answer matches. */
  jumpIf?: JumpIf;
  /** Routes individual answers to their own next question; the first match wins, after `jumpIf`. */
  jumpRules?: JumpIf[];
  validation?: Validation;
  scale?: Scale;
  placeholder?: string;
  /** Set on loop & merge copies while answering; never saved in a definition. */
  loop?: LoopContext;
}

export interface LoopContext {
  /** Id of the question this copy repeats. */
  baseId: string;
  /** Option of the loop question this copy is for. */
  optionId: string;
  label: string;
}

/**
 * How questions are grouped into pages for the respondent:
 * - question: one question per page (default),
 * - section: consecutive questions sharing a section title share a page;
 *   questions without a section title stay on their own page,
 * - all: every question on a single page.
 */
export type PageMode = 'question' | 'section' | 'all';

export const PAGE_MODES: PageMode[] = ['question', 'section', 'all'];

/** Where respondents see the human check; omitted means "both". */
export type CaptchaPlacement = 'both' | 'start' | 'end';
export const CAPTCHA_PLACEMENTS: CaptchaPlacement[] = ['both', 'start', 'end'];

/** Per-section randomization overrides, keyed by the trimmed section title. */
export interface SectionOptions {
  /** Keep this section's questions in their written order even when questions are shuffled. */
  keepQuestionOrder?: boolean;
  /** Keep this section in its position when section order is shuffled. */
  keepPosition?: boolean;
  /**
   * Loop & merge: repeat this section once for each option chosen in this
   * earlier multiple-choice question. Answers are stored as "<questionId>@<optionId>".
   */
  loopOver?: string;
}

/**
 * A randomizer shows each respondent only `present` of its sections, in random
 * order, in the place the sections have in the flow. `sections` are section
 * titles; they must be adjacent in the survey flow.
 */
export interface Randomizer {
  id: string;
  sections: string[];
  present: number;
  /** Only while the randomizer has no sections: the section it sits under in the flow editor. */
  after?: string;
  /** Show the sections that fewer respondents have been given so far, instead of a plain random pick. */
  evenly?: boolean;
  /** Most respondents to give each section (by title). Absent means no limit. */
  limits?: Record<string, number>;
  /** ISO time from which responses are counted; the counts start over when it is set. */
  resetAt?: string;
}

/** Respondents given each section so far, by randomizer id and section title. */
export type FlowCounts = Record<string, Record<string, number>>;

/**
 * Answer keys starting with this prefix carry a respondent's randomizer picks:
 * `$flow:<randomizerId>` holds the picked section titles in presentation order.
 * They live in the answers so every consumer (resolve, validation, analysis)
 * sees the same sections the respondent did.
 */
export const FLOW_KEY_PREFIX = '$flow:';
export const flowKey = (randomizerId: string): string => FLOW_KEY_PREFIX + randomizerId;
export const isFlowKey = (key: string): boolean => key.startsWith(FLOW_KEY_PREFIX);

export interface Settings {
  /** Shuffle questions within each section (or within each run of untitled questions). */
  randomizeQuestions: boolean;
  /** Show each respondent only some of a group of sections (see Randomizer). */
  randomizers?: Randomizer[];
  /** Shuffle the order of titled sections. Untitled questions stay where they are. */
  randomizeSections?: boolean;
  sectionOptions?: Record<string, SectionOptions>;
  showProgress: boolean;
  allowBack: boolean;
  /** Show the respondent their elapsed (active) time. Display only; nothing is time-limited. */
  showTimer?: boolean;
  /** Page grouping; omitted means one question per page. */
  pageMode?: PageMode;
  /** Human check placement when the server requires Turnstile; omitted means "both". */
  captcha?: CaptchaPlacement;
  /**
   * Hidden fields filled from the survey link's query string (?region=jkt).
   * Stored with the response and usable in piping as {{region}}.
   */
  hiddenFields?: string[];
}

export interface Definition {
  questions: Question[];
  settings: Settings;
  welcomeMessage?: string;
  thankYouMessage?: string;
  endings?: Ending[];
  quotas?: Quota[];
  /** Language code of the base text (default "en"). */
  language?: string;
  /** Extra languages respondents can switch to, by language code. */
  translations?: Record<string, Translation>;
  theme?: Theme;
}

/**
 * Object answers: matrix (row id -> option id or ids), constant sum (item id ->
 * number), text form (field id -> text), file upload ({url, name, size, type})
 * and location ({lat, lng, accuracy}).
 */
export type AnswerRecord = { [key: string]: string | number | string[] | null };

/**
 * single/dropdown: option id; multiple/ranking: option ids; yes_no: boolean;
 * number/rating/slider/nps: number; date: YYYY-MM-DD; signature: PNG data URL;
 * matrix, constant sum, text form, file and location: AnswerRecord.
 */
export type AnswerValue = string | number | boolean | string[] | AnswerRecord | null;
export type Answers = Record<string, AnswerValue>;

/** Suffix of the answer key holding "Other, please specify" text. */
export const OTHER_SUFFIX = ':other';
export const otherKey = (questionId: string) => questionId + OTHER_SUFFIX;

export interface Issue {
  questionId?: string;
  message: string;
}

/** Payload accepted by POST /api/public/surveys/{code}/responses and the mobile sync batch. */
export interface Submission {
  clientSubmissionId: string;
  /** Named roster entry selected by an assigned field enumerator. */
  respondentRecordId?: string;
  surveyVersion: number;
  answers: Answers;
  /** Active milliseconds spent on each question. */
  timings: Record<string, number>;
  /** Order the questions were presented in, after randomization. */
  questionOrder: string[];
  startedAt: string;
  submittedAt: string;
  /** Active (foreground) milliseconds for the whole response. */
  durationMs: number;
  /** Hidden-field values captured from the link. */
  embedded?: Record<string, string>;
  /** Language the respondent answered in. */
  language?: string;
  /** Ending reached through a jump rule. */
  endingId?: string;
  /** Personal invitation token from the survey link; one response per token. */
  inviteToken?: string;
}

export interface PublicSurvey {
  id: string;
  /** Running questionnaire number; display with formatSurveyNumber. */
  number: number;
  code: string;
  title: string;
  description: string;
  version: number;
  requireAuth: boolean;
  definition: Definition;
  publishedAt: string;
  respondents?: { id: string; name: string; externalId: string; gender: string; groupName?: string }[];
  /** Quotas that are already full; matching respondents are screened out. */
  fullQuotas?: string[];
  /** How many respondents each section of a balanced or limited randomizer has been given. */
  flowCounts?: FlowCounts;
  /** When the survey stops accepting responses, if scheduled. */
  closesAt?: string;
  /** Name on the personal invitation link the survey was opened with. */
  invitee?: string;
}

export const QUESTION_TYPES: { type: QuestionType; label: string; hint: string }[] = [
  { type: 'short_text', label: 'Short text', hint: 'One line answer' },
  { type: 'long_text', label: 'Long text', hint: 'Paragraph answer' },
  { type: 'single_choice', label: 'Single choice', hint: 'Pick one option' },
  { type: 'multiple_choice', label: 'Multiple choice', hint: 'Pick several options' },
  { type: 'ranking', label: 'Ranking', hint: 'Order options from most to least preferred' },
  { type: 'dropdown', label: 'Dropdown', hint: 'Pick one from a list' },
  { type: 'number', label: 'Number', hint: 'Numeric answer' },
  { type: 'rating', label: 'Rating', hint: 'Scale, e.g. 1–5' },
  { type: 'date', label: 'Date', hint: 'Calendar date' },
  { type: 'yes_no', label: 'Yes / No', hint: 'Binary answer' },
  { type: 'matrix', label: 'Matrix table', hint: 'Several statements on one scale' },
  { type: 'slider', label: 'Slider', hint: 'Drag to a value' },
  { type: 'nps', label: 'Net Promoter Score', hint: 'Likelihood to recommend, 0–10' },
  { type: 'constant_sum', label: 'Constant sum', hint: 'Split a total between items' },
  { type: 'text_form', label: 'Text form', hint: 'Several labelled text boxes' },
  { type: 'file_upload', label: 'File upload', hint: 'Photo or document' },
  { type: 'signature', label: 'Signature', hint: 'Draw a signature' },
  { type: 'location', label: 'Location', hint: 'GPS coordinates' },
  { type: 'descriptive', label: 'Text / instructions', hint: 'Information only, no answer' },
];

export const OPERATORS: { op: Operator; label: string; needsValue: boolean }[] = [
  { op: 'equals', label: 'is', needsValue: true },
  { op: 'not_equals', label: 'is not', needsValue: true },
  { op: 'contains', label: 'contains', needsValue: true },
  { op: 'not_contains', label: 'does not contain', needsValue: true },
  { op: 'gt', label: 'is greater than', needsValue: true },
  { op: 'gte', label: 'is at least', needsValue: true },
  { op: 'lt', label: 'is less than', needsValue: true },
  { op: 'lte', label: 'is at most', needsValue: true },
  { op: 'answered', label: 'is answered', needsValue: false },
  { op: 'not_answered', label: 'is not answered', needsValue: false },
];

export function hasOptions(type: QuestionType): boolean {
  return (
    type === 'single_choice' ||
    type === 'multiple_choice' ||
    type === 'ranking' ||
    type === 'dropdown' ||
    type === 'matrix' ||
    type === 'constant_sum' ||
    type === 'text_form'
  );
}

/** Option-list types whose options are picked (and so can be carried forward, scored or "Other"). */
export function isChoice(type: QuestionType): boolean {
  return type === 'single_choice' || type === 'multiple_choice' || type === 'dropdown';
}

/** Types that collect no answer. */
export function isDisplayOnly(type: QuestionType): boolean {
  return type === 'descriptive';
}

export function effectiveScale(q: Question): Scale {
  if (q.type === 'nps') return { min: 0, max: 10, minLabel: q.scale?.minLabel, maxLabel: q.scale?.maxLabel };
  if (q.type === 'slider') return q.scale ?? { min: 0, max: 100 };
  return q.scale ?? { min: 1, max: 5 };
}

export const DEFAULT_TOTAL = 100;

/** Upload limit for respondents' files; mirrored by the API. */
export const MAX_UPLOAD_BYTES = 10 << 20;
/** Signatures are stored inline as PNG data URLs. */
export const MAX_SIGNATURE_LENGTH = 200_000;

const UPLOADED_FILE = /^\/api\/uploads\/[a-f0-9]{32}\.[a-z0-9]{1,5}$/;

/** True for a file uploaded by a respondent ("/api/uploads/<name>"). */
export function isUploadUrl(url: unknown): boolean {
  return typeof url === 'string' && UPLOADED_FILE.test(url);
}

const LANGUAGE = /^[a-z]{2,3}(-[A-Za-z]{2,4})?$/;
export function isLanguageCode(code: string): boolean {
  return LANGUAGE.test(code);
}

/** Languages offered in the builder; any valid code is accepted. */
export const SURVEY_LANGUAGES: { code: string; name: string }[] = [
  { code: 'en', name: 'English' },
  { code: 'id', name: 'Bahasa Indonesia' },
  { code: 'jv', name: 'Basa Jawa' },
  { code: 'su', name: 'Basa Sunda' },
  { code: 'ms', name: 'Bahasa Melayu' },
  { code: 'zh', name: '中文' },
  { code: 'ar', name: 'العربية' },
  { code: 'th', name: 'ไทย' },
  { code: 'vi', name: 'Tiếng Việt' },
  { code: 'tl', name: 'Filipino' },
  { code: 'ja', name: '日本語' },
  { code: 'ko', name: '한국어' },
  { code: 'hi', name: 'हिन्दी' },
  { code: 'es', name: 'Español' },
  { code: 'fr', name: 'Français' },
  { code: 'de', name: 'Deutsch' },
  { code: 'pt', name: 'Português' },
];

export function languageName(code: string): string {
  return SURVEY_LANGUAGES.find((l) => l.code === code)?.name ?? code;
}

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;
export function isHexColor(v: string | undefined): boolean {
  return !!v && HEX_COLOR.test(v);
}

const FIELD_NAME = /^[A-Za-z][A-Za-z0-9_]{0,31}$/;
export function isFieldName(name: string): boolean {
  return FIELD_NAME.test(name);
}

/** Matrix/constant-sum/form rows or items: matrix rows, otherwise the options. */
export function subItems(q: Question): Option[] {
  return q.type === 'matrix' ? (q.rows ?? []) : q.type === 'constant_sum' || q.type === 'text_form' ? (q.options ?? []) : [];
}

export const MAX_TOOLTIP_LENGTH = 500;

/** Upload limits; mirrored by the API. */
export const MAX_IMAGE_BYTES = 5 << 20;
export const MAX_VIDEO_BYTES = 200 << 20;

const UPLOADED_MEDIA = /^\/api\/media\/[a-f0-9]{32}\.(jpg|png|webp|gif|mp4|webm|mov)$/;

/** True for an uploaded file path or an https URL. Mirrors survey.validMediaURL in Go. */
export function isValidMediaUrl(url: string | undefined): boolean {
  if (!url || url.length > 2000) return false;
  if (UPLOADED_MEDIA.test(url)) return true;
  try {
    const u = new URL(url);
    return u.protocol === 'https:' && !!u.hostname;
  } catch {
    return false;
  }
}

export type VideoSource = { kind: 'youtube'; id: string } | { kind: 'vimeo'; id: string } | { kind: 'file'; url: string };

/** Works out how to play a video URL: YouTube, Vimeo, or a direct/uploaded file. */
export function videoSource(url: string): VideoSource {
  try {
    const u = new URL(url, 'https://local.invalid');
    const host = u.hostname.replace(/^(www\.|m\.)/, '');
    if (host === 'youtu.be') {
      const id = u.pathname.slice(1).split('/')[0];
      if (id) return { kind: 'youtube', id };
    }
    if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
      const v = u.searchParams.get('v');
      if (v) return { kind: 'youtube', id: v };
      const m = u.pathname.match(/^\/(embed|shorts|live)\/([\w-]+)/);
      if (m) return { kind: 'youtube', id: m[2]! };
    }
    if (host === 'vimeo.com' || host === 'player.vimeo.com') {
      const m = u.pathname.match(/(\d{5,})/);
      if (m) return { kind: 'vimeo', id: m[1]! };
    }
  } catch {
    // fall through to a plain file
  }
  return { kind: 'file', url };
}

/** Resolves uploaded media ("/api/...") against an API base URL; external URLs pass through. */
export function mediaSrc(url: string, apiBase = ''): string {
  return url.startsWith('/') ? apiBase.replace(/\/$/, '') + url : url;
}

/** Formats a questionnaire's running number, e.g. 7 -> "Q-0007". Mirrors survey.FormatNumber in Go. */
export function formatSurveyNumber(n: number): string {
  return `Q-${String(n).padStart(4, '0')}`;
}

export function emptyDefinition(): Definition {
  return { questions: [], settings: { randomizeQuestions: false, showProgress: true, allowBack: true } };
}
